import { useRef, type ReactNode } from 'react'
import { Button } from './Button'
import { Modal, ModalBody, ModalFooter } from './Modal'

interface Props {
  open: boolean
  title: string
  /** What is about to happen, in plain words. Name the thing being acted on —
   *  "Palakkad on 29 Sep" tells you far more than "this item". */
  children: ReactNode
  confirmLabel: string
  /** Override when plain "Cancel" would be ambiguous — cancelling an
   *  appointment has two buttons that would otherwise both read "Cancel". */
  cancelLabel?: string
  onConfirm: () => void
  onCancel: () => void
  loading?: boolean
  /** Red confirm button. For anything that takes something away. */
  destructive?: boolean
}

/** A stop before something that cannot be undone. */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  loading = false,
  destructive = false,
}: Props) {
  const cancel = useRef<HTMLDivElement>(null)

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      // Focus lands on "keep it", so a stray Enter or space cannot destroy
      // anything. Button does not forward a ref, hence the wrapper.
      initialFocus={{ get current() { return cancel.current?.querySelector('button') ?? null } }}
    >
      <ModalBody>
        <div className="text-sm leading-normal text-fg-muted">{children}</div>
      </ModalBody>
      <ModalFooter>
        <div ref={cancel}>
          <Button variant="secondary" onClick={onCancel} type="button">
            {cancelLabel}
          </Button>
        </div>
        <Button
          variant={destructive ? 'destructive' : 'primary'}
          onClick={onConfirm}
          loading={loading}
          type="button"
        >
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
